import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, CheckCircle2, ArrowLeft } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Use at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords don't match");
    if (!token) return toast.error("Missing reset token — request a new link");
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2000);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <PublicNav />
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          {done ? (
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8 text-center" data-testid="reset-success">
              <div className="w-14 h-14 rounded-full bg-brand-greenSoft grid place-items-center mx-auto">
                <CheckCircle2 size={28} className="text-brand-greenDark" />
              </div>
              <h2 className="mt-4 font-heading font-semibold text-xl text-brand-navy">Password updated</h2>
              <p className="mt-2 text-sm text-slate-600">Taking you to the sign-in page…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8" data-testid="reset-form">
              <div className="w-11 h-11 rounded-lg bg-brand-blueSoft text-brand-blue grid place-items-center">
                <KeyRound size={20} />
              </div>
              <h1 className="mt-4 font-heading font-semibold text-2xl text-brand-navy">Set a new password</h1>
              <p className="mt-2 text-sm text-slate-600">
                Pick something you'll remember. At least 8 characters.
              </p>
              <label className="mt-6 block text-xs font-semibold text-slate-600">New password</label>
              <div className="relative mt-1">
                <input required type={show ? "text" : "password"} data-testid="reset-pw"
                  value={pw} onChange={(e) => setPw(e.target.value)}
                  className="pr-10 pl-3 py-3 w-full rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-brand-blue" />
                <button type="button" onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <label className="mt-4 block text-xs font-semibold text-slate-600">Confirm password</label>
              <input required type={show ? "text" : "password"} data-testid="reset-pw2"
                value={pw2} onChange={(e) => setPw2(e.target.value)}
                className="mt-1 pl-3 pr-3 py-3 w-full rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-brand-blue" />
              <button type="submit" disabled={submitting || !token} data-testid="reset-submit"
                className="mt-6 w-full py-3 rounded-md bg-brand-blue text-white font-semibold hover:bg-brand-blueDark disabled:opacity-60">
                {submitting ? "Updating…" : "Update password"}
              </button>
              {!token && (
                <div className="mt-3 rounded bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
                  No reset token in this link — request a new one from{" "}
                  <Link to="/forgot-password" className="underline font-semibold">Forgot password</Link>.
                </div>
              )}
              <Link to="/login"
                className="mt-4 flex items-center justify-center gap-1 text-sm text-slate-600 hover:text-brand-navy">
                <ArrowLeft size={13} /> Back to sign in
              </Link>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
